// Claire platform types (app). Mirrors functions/src/claire/types.ts — keep in sync.
import type { Timestamp } from 'firebase/firestore';

export type RiskLevel = 'green' | 'yellow' | 'red';
export type AutonomyLevel = 'inform' | 'suggest' | 'ask' | 'autonomous';
export type ApprovalMode = 'never' | 'always' | 'by_risk' | 'disabled';
export type ModelRole =
  | 'PRIMARY_REASONING'
  | 'FAST_TASK'
  | 'PRODUCT_REVIEW'
  | 'VISION'
  | 'VOICE'
  | 'EMBEDDINGS'
  | 'MEETING_REASONING'
  | 'FALLBACK';

export const MODEL_ROLES: { role: ModelRole; label: string; hint: string }[] = [
  { role: 'PRIMARY_REASONING', label: 'Primary reasoning', hint: 'Conversations, questions and tool use' },
  { role: 'FAST_TASK', label: 'Fast tasks', hint: 'Summaries, titles and quick classification' },
  { role: 'PRODUCT_REVIEW', label: 'Product review', hint: 'Milestone 4' },
  { role: 'VISION', label: 'Vision', hint: 'Image inspection' },
  { role: 'VOICE', label: 'Listening (speech-to-text)', hint: 'Transcribes what people say. Claire speaks with ElevenLabs — see Identity & Voice.' },
  { role: 'EMBEDDINGS', label: 'Embeddings', hint: 'Semantic knowledge search' },
  { role: 'MEETING_REASONING', label: 'Meetings', hint: 'Milestone 8' },
  { role: 'FALLBACK', label: 'Fallback', hint: 'Used if the primary model errors' },
];

export interface ModelAssignment {
  provider: 'openai';
  model: string;
  reasoningEffort?: 'minimal' | 'low' | 'medium' | 'high';
  maxOutputTokens?: number;
}

export interface BrainConfig {
  identity: { name: string; role: string; purpose: string; instructions: string };
  behavior: Record<
    | 'communicationStyle'
    | 'responseLength'
    | 'initiative'
    | 'escalation'
    | 'uncertainty'
    | 'toolUsage'
    | 'dataHandling'
    | 'meetingBehavior'
    | 'emailBehavior'
    | 'productReviewBehavior',
    string
  >;
  defaultAutonomy: AutonomyLevel;
}

export const BEHAVIOR_FIELDS: { key: keyof BrainConfig['behavior']; label: string }[] = [
  { key: 'communicationStyle', label: 'Communication style' },
  { key: 'responseLength', label: 'Response length' },
  { key: 'initiative', label: 'Initiative' },
  { key: 'escalation', label: 'Escalation' },
  { key: 'uncertainty', label: 'When uncertain' },
  { key: 'toolUsage', label: 'Tool usage' },
  { key: 'dataHandling', label: 'Data handling' },
  { key: 'meetingBehavior', label: 'Meetings' },
  { key: 'emailBehavior', label: 'Email' },
  { key: 'productReviewBehavior', label: 'Product review' },
];

export interface BrainVersion {
  id: string;
  version: number;
  createdAt?: Timestamp;
  createdBy: string;
  changeSummary: string;
  configurationSnapshot: BrainConfig;
  active: boolean;
}

export interface ClaireSkill {
  id: string;
  name: string;
  description: string;
  category: string;
  enabled: boolean;
  instructions: string;
  triggers: string[];
  requiredRoles: string[];
  availableTools: string[];
  modelRole: ModelRole;
  autonomyLevel: AutonomyLevel;
  requiresApproval: boolean;
  riskLevel: RiskLevel;
  createdBy?: string;
  updatedAt?: Timestamp;
}

export interface ToolMeta {
  name: string;
  displayName: string;
  description: string;
  category: string;
  riskLevel: RiskLevel;
  defaultApproval: ApprovalMode;
  requiredPaths?: string[];
  requiredRoles?: string[];
  requiredConnection?: string;
  eventType: string;
  status: string;
  parameters: { properties: Record<string, { type: string; description?: string; enum?: string[] }>; required: string[] };
}

export interface ToolPolicy {
  enabled: boolean;
  riskLevel?: RiskLevel;
  approvalMode?: ApprovalMode;
  allowedRoles?: string[];
  updatedBy?: string;
}

export interface PermissionsConfig {
  accessRoles: string[];
  maxAutonomyByRole: Record<string, AutonomyLevel>;
  approvalByRisk: Record<RiskLevel, 'auto' | 'confirm' | 'elevated'>;
  teachRoles: string[];
  maturity: MaturityLevel;
}

export type ClaireMode = 'ask' | 'do' | 'watch';

export const CLAIRE_MODES: { id: ClaireMode; label: string; hint: string }[] = [
  { id: 'ask', label: 'Ask', hint: 'Questions and lookups. Changes nothing.' },
  { id: 'do', label: 'Do', hint: 'Work you delegate. Changes are confirmed first.' },
  { id: 'watch', label: 'Watch', hint: 'Set up monitoring you authorize.' },
];

export const CLAIRE_MODE_KEY = 'claire_mode';
/** The mode this person last picked on this computer; Do until they choose otherwise. */
export const savedClaireMode = (): ClaireMode => {
  const saved = typeof window !== 'undefined' ? window.localStorage.getItem(CLAIRE_MODE_KEY) : null;
  return CLAIRE_MODES.some((m) => m.id === saved) ? (saved as ClaireMode) : 'do';
};

export type MaturityLevel = 'knowledge' | 'execution' | 'analysis' | 'advisory';

export const MATURITY_OPTIONS: { id: MaturityLevel; label: string; hint: string }[] = [
  { id: 'knowledge', label: 'Knowledge', hint: 'Answers questions and finds information. Never changes anything.' },
  { id: 'execution', label: 'Execution', hint: 'Knowledge, plus completing work people explicitly delegate.' },
  { id: 'analysis', label: 'Analysis', hint: 'Adds summaries, comparisons and trends of FIN data — facts only.' },
  { id: 'advisory', label: 'Advisory', hint: 'Adds opinions and recommendations, always labelled as such.' },
];

export interface PronunciationRule {
  term: string;
  say: string;
}

export interface VoiceConfig {
  provider: 'elevenlabs';
  voiceId: string;
  voiceName: string;
  modelId: string;
  realtimeModelId: string;
  settings: { stability: number; similarityBoost: number; style: number; useSpeakerBoost: boolean; speed: number };
  outputFormat: string;
  pronunciation: PronunciationRule[];
  spokenStyle: string;
}

export interface PresenceConfig {
  avatarUrl: string;
  meetingImageUrl: string;
  meetingGreeting: string;
  avatar: { provider: 'tavus'; enabled: boolean; faceId: string };
  voice: VoiceConfig;
}

export interface ClaireMeeting {
  id: string;
  ownerUid: string;
  ownerName: string;
  meetingUrl: string;
  platform: string;
  botId: string;
  sessionId: string;
  status: 'joining' | 'waiting_room' | 'in_call' | 'leaving' | 'ended' | 'failed';
  canSpeak: boolean;
  face?: boolean;
  summary?: string | null;
  error?: string | null;
  brief?: string | null;
  participation?: MeetingParticipation;
  alertWhen?: string | null;
  reportFocus?: string | null;
  createdAt?: Timestamp;
  endedAt?: Timestamp;
}

export type MeetingParticipation = 'silent' | 'addressed' | 'active';

export interface MeetingLine {
  id: string;
  speaker: string;
  text: string;
  createdAt?: Timestamp;
}

export interface ElevenVoice {
  voiceId: string;
  name: string;
  category: string;
  previewUrl: string | null;
  description: string;
}

export interface ClaireWatch {
  id: string;
  title: string;
  condition: string;
  frequency: 'hourly' | 'daily' | 'weekly';
  notify: string;
  ownerUid: string;
  ownerName: string;
  status: 'proposed' | 'active' | 'paused' | 'ended';
  authorizedBy: string | null;
  authorizedAt?: Timestamp | null;
  endsOn: string;
  sessionId: string | null;
  createdAt?: Timestamp;
}

export interface ClaireIdentity {
  id: string;
  channel: 'slack' | 'phone';
  externalId: string;
  uid: string;
  verifiedBy: 'slack_email' | 'phone_otp' | 'master_admin';
  label: string;
  createdAt?: Timestamp;
}

export interface KnowledgeVersion {
  id: string;
  version: number;
  title: string;
  content: string;
  category: string;
  authority: KnowledgeAuthority;
  status: string;
  editedBy: string;
  editedAt?: Timestamp | null;
  changeNote: string;
}

export type KnowledgeAuthority =
  | 'official_policy'
  | 'approved_procedure'
  | 'management_decision'
  | 'training_material'
  | 'reference'
  | 'claire_note';

export const AUTHORITY_OPTIONS: { id: KnowledgeAuthority; label: string }[] = [
  { id: 'official_policy', label: 'Official policy' },
  { id: 'approved_procedure', label: 'Approved procedure' },
  { id: 'management_decision', label: 'Management decision' },
  { id: 'training_material', label: 'Training material' },
  { id: 'reference', label: 'Reference' },
  { id: 'claire_note', label: 'Claire note' },
];

export const KNOWLEDGE_CATEGORIES = [
  'Company', 'Departments', 'Employees', 'Locations', 'Terminology', 'Operations', 'Inventory', 'Photography',
  'Editing', 'Templates', 'Product Review', 'Publishing', 'Shipping', 'Repairs', 'Store Operations', 'Policies',
  'Training', 'Blocks', 'Pebbles', 'Management Decisions', 'Product Standards', 'Special Instructions', 'Exceptions',
];

export interface KnowledgeEntry {
  id: string;
  title: string;
  category: string;
  content: string;
  source: string;
  authority: KnowledgeAuthority;
  priority: number;
  status: 'active' | 'draft' | 'pending' | 'retired';
  tags: string[];
  effectiveDate?: string | null;
  expirationDate?: string | null;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  version?: number;
  changeNote?: string;
}

export interface MemoryEntry {
  id: string;
  scope: 'company' | 'user' | 'session';
  ownerUid: string | null;
  ownerName: string;
  content: string;
  source: string;
  status: 'active' | 'pending' | 'retired';
  createdAt?: Timestamp;
  lastUsed?: Timestamp | null;
}

export interface ClaireSession {
  id: string;
  userId: string;
  title: string;
  source: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  messageCount: number;
  liveStatus?: string | null;
  lastMessage?: string;
}

export interface ClaireMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  createdAt?: Timestamp;
  status?: string;
  mode?: ClaireMode;
  toolCalls?: { name: string; outcome: string }[];
  /** Set on the follow-up Claire posts after an approval is decided. */
  approvalId?: string;
  /** Photos attached to a user message (download URLs). */
  images?: string[];
  /** Set on the message Claire posts when pricing research finishes. */
  researchId?: string;
  folderJobId?: string;
  folderListJobId?: string;
  /** Client-only: shown before the server copy arrives. */
  pending?: boolean;
}

export interface ToolCallRecord {
  name: string;
  outcome: 'ran' | 'blocked' | 'approval' | 'error';
  risk: RiskLevel;
  durationMs: number;
  detail?: string;
}

export interface ClaireActivity {
  id: string;
  timestamp?: Timestamp;
  userId: string;
  userName: string;
  sessionId: string | null;
  source: string;
  eventType: string;
  requestSummary: string;
  responseSummary: string;
  skillIds: string[];
  toolIds: string[];
  toolCalls?: ToolCallRecord[];
  modelUsed: string | null;
  recordsAccessed: string[];
  recordsModified: string[];
  externalServicesUsed: string[];
  riskLevel: RiskLevel;
  approvalRequired: boolean;
  approvedBy: string | null;
  status: 'success' | 'blocked' | 'error' | 'pending_approval';
  durationMs: number;
  tokens?: { input: number; output: number } | null;
  brainVersion?: number;
  errorDetails?: string | null;
}

export interface ClaireApproval {
  id: string;
  requestedBy: string;
  requestedByName: string;
  tool: string;
  displayName?: string;
  preview: string;
  sessionId?: string | null;
  risk: RiskLevel;
  level: 'confirm' | 'elevated';
  status: 'pending' | 'running' | 'approved' | 'rejected' | 'expired' | 'failed';
  decidedByName?: string;
  error?: string | null;
  createdAt?: Timestamp;
}
